import React, { useState } from 'react'
import { ScrollView, StyleSheet, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import type { NativeStackScreenProps } from '@react-navigation/native-stack'
import { Button, Header, KeyboardAwareView, Screen, Text, TextInput } from '@components'
import { useLanguage } from '@context'
import { Theme } from '@theme'
import { useSubmitOrganizerRequest } from '@hooks'
import type { EventsStackParamList } from '../../../navigation/navigator/EventsStackNavigator'

type Props = NativeStackScreenProps<EventsStackParamList, 'OrganizerRequest'>

const isHttpUrl = (value: string) => {
  try {
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

/** Request form: who you organize for, why, and an optional website. */
export const OrganizerRequestForm: React.FC<Props> = ({ navigation }) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)
  const submit = useSubmitOrganizerRequest()
  const [organizationName, setOrganizationName] = useState('')
  const [message, setMessage] = useState('')
  const [websiteUrl, setWebsiteUrl] = useState('')
  const [showErrors, setShowErrors] = useState(false)

  const orgError = organizationName.trim().length < 2
  const messageError = message.trim().length < 1
  const websiteError = websiteUrl.trim() !== '' && !isHttpUrl(websiteUrl.trim())

  const onSubmit = () => {
    setShowErrors(true)
    if (orgError || messageError || websiteError) return
    submit.mutate(
      {
        organizationName: organizationName.trim(),
        message: message.trim(),
        websiteUrl: websiteUrl.trim() || undefined,
      },
      { onSuccess: () => navigation.navigate('EventsList') },
    )
  }

  return (
    <Screen>
      <Header
        title={t('admin.request.formTitle')}
        iconLeft="arrowBack"
        onPressLeft={() => navigation.navigate('EventsList')}
      />
      <KeyboardAwareView isFullScreen androidOnly offsetTopInset>
        <ScrollView
          contentContainerStyle={styles.scroll}
          keyboardShouldPersistTaps="handled"
          // iOS: the keyboard never hides the message field or "Envoyer ma demande".
          automaticallyAdjustKeyboardInsets
        >
          <View style={styles.content}>
            <Text type="body" color={theme.colors.greyBlue}>
              {t('admin.request.formIntro')}
            </Text>
            <TextInput
              testID="organizer-request-org"
              title={t('admin.request.organizationLabel')}
              value={organizationName}
              onChangeText={setOrganizationName}
              maxLength={120}
              hasError={showErrors && orgError}
              errorMessage={
                showErrors && orgError ? t('admin.request.organizationError') : undefined
              }
            />
            <TextInput
              testID="organizer-request-message"
              title={t('admin.request.messageLabel')}
              value={message}
              onChangeText={setMessage}
              maxLength={1000}
              isMultiline
              multiline
              hasError={showErrors && messageError}
              errorMessage={
                showErrors && messageError ? t('admin.request.messageError') : undefined
              }
            />
            <TextInput
              testID="organizer-request-website"
              title={t('admin.request.websiteLabel')}
              value={websiteUrl}
              onChangeText={setWebsiteUrl}
              autoCapitalize="none"
              keyboardType="url"
              hasError={showErrors && websiteError}
              errorMessage={
                showErrors && websiteError ? t('admin.request.websiteError') : undefined
              }
            />
            <Button
              title={t('admin.request.send')}
              disabled={submit.isPending}
              onPress={onSubmit}
            />
          </View>
        </ScrollView>
      </KeyboardAwareView>
    </Screen>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    scroll: { padding: theme.spacing.xl, alignItems: 'center' },
    content: { width: '100%', maxWidth: 560, gap: theme.spacing.lg },
  })
